import React, { memo, useEffect, useState } from 'react';
import { Button, Form, Input, ModalBase, Textarea } from '@bynder/design-system';
import { useTranslate } from '@bynder/localization';
import { ComponentModalMode } from './types';
import { CollectionSelector } from './CollectionSelector';
import { PartialComponent } from 'packages/services/ComponentService';

const MAX_NAME_LENGTH = 50;

interface Props {
    isOpen: boolean;
    onClose: () => void;
    onSave: (params: {
        name: string;
        description: string;
        collectionId: number | null;
        collectionName?: string;
        id?: number;
    }) => void;
    mode: ComponentModalMode;
    component?: PartialComponent;
    suggestedName?: string;
    isSaving: boolean;
}

const ComponentModal = ({ isOpen, onClose, onSave, mode, component, suggestedName, isSaving }: Props) => {
    const { translate } = useTranslate();

    const isCreateMode = mode === 'create';
    const [page, setPage] = useState<'component' | 'collection'>('component');

    const [name, setName] = useState(isCreateMode ? suggestedName || '' : component?.name || '');
    const isLongName = name.length > MAX_NAME_LENGTH;
    const isEmptyName = !name.trim();

    const [description, setDescription] = useState(isCreateMode ? '' : component?.description || '');
    const MAX_DESCRIPTION_LENGTH = 120;
    const isLongDescription = description.length > MAX_DESCRIPTION_LENGTH;

    const [collectionId, setCollectionId] = useState<number | null>(null);

    const [collectionName, setCollectionName] = useState('');
    const [collectionNameDirty, setCollectionNameDirty] = useState(false);
    const isLongCollectionName = collectionName.length > MAX_NAME_LENGTH;
    const isEmptyCollectionName = !collectionName.trim();

    const isSaveDisabled = () => {
        const defaultChecks = isEmptyName || isLongName || isLongDescription || collectionId === null || isSaving;

        if (mode === 'create') {
            return defaultChecks;
        }

        const hasChanges = Object.values({
            name: component?.name !== name?.trim(),
            description: component?.description
                ? description?.trim() !== component.description
                : description?.trim() !== '',
            collectionId: component?.collectionId !== collectionId,
        }).some(Boolean);

        return defaultChecks || !hasChanges;
    };

    useEffect(() => {
        if (isOpen) {
            setPage('component');
            setName(isCreateMode ? suggestedName || '' : component?.name || '');
            setDescription(isCreateMode ? '' : component?.description || '');
            setCollectionId(isCreateMode ? null : component?.collectionId || null);
            setCollectionName('');
            setCollectionNameDirty(false);
        }
    }, [isOpen, mode, suggestedName, component]);

    return (
        <ModalBase isOpen={isOpen} onClose={onClose}>
            {page === 'component' && (
                <>
                    <ModalBase.Header
                        title={
                            isCreateMode
                                ? translate('components.create.modal.title')
                                : translate('components.overview.componentEditModal.title')
                        }
                    />
                    <ModalBase.Content>
                        <Form onSubmit={(e) => e.preventDefault()}>
                            <Form.Group>
                                <Form.Label htmlFor="component-name">
                                    {translate('components.create.modal.name.label')}
                                </Form.Label>
                                <Input
                                    value={name}
                                    type="text"
                                    placeholder={translate('components.create.modal.name.placeholder')}
                                    id="component-name"
                                    aria-describedby="component-name-helper-text"
                                    hasClearButton
                                    clearButtonProps={{
                                        'aria-label': translate('components.create.modal.name.clearButton'),
                                    }}
                                    onChange={(value) => setName(value)}
                                    isInvalid={isEmptyName || isLongName}
                                />
                                {(isEmptyName || isLongName) && (
                                    <Form.HelperText id="component-name-helper-text" isInvalid>
                                        {isLongName
                                            ? translate('components.create.modal.name.error.too-long')
                                            : translate('components.create.modal.name.error.empty')}
                                    </Form.HelperText>
                                )}
                            </Form.Group>
                            <Form.Group>
                                <Form.Label
                                    htmlFor="component-description"
                                    secondaryText={translate('components.create.modal.description.label.secondary')}
                                >
                                    {translate('components.create.modal.description.label')}
                                </Form.Label>
                                <Textarea
                                    value={description}
                                    placeholder={translate('components.create.modal.description.placeholder')}
                                    id="component-description"
                                    onChange={(value) => setDescription(value)}
                                    isInvalid={isLongDescription}
                                />
                                {isLongDescription && (
                                    <Form.HelperText id="component-description-helper-text" isInvalid>
                                        {translate('components.create.modal.description.error.too-long')}
                                    </Form.HelperText>
                                )}
                            </Form.Group>
                            <Form.Group>
                                <Form.Label id="component-collection-selector">
                                    {mode === 'create'
                                        ? translate('components.create.modal.collection.selector.label')
                                        : translate('components.create.modal.collection-edit.selector.label')}
                                </Form.Label>
                                <CollectionSelector
                                    selectedCollectionId={collectionId}
                                    onChange={(option) => {
                                        setCollectionId((prevSelected) => {
                                            // Deselect if previously selected, otherwise select new option
                                            return prevSelected === option ? null : option;
                                        });
                                    }}
                                    isOpen={isOpen}
                                    ariaLabelledBy="component-collection-selector"
                                />
                            </Form.Group>
                            <Form.Group>
                                {mode === 'create' && (
                                    <Form.Label id="component-new-collection">
                                        {translate('components.create.modal.collection.new.label')}
                                    </Form.Label>
                                )}
                                <Button
                                    variant="primary"
                                    aria-labelledby="component-new-collection"
                                    isDisabled={isEmptyName || isLongName || isLongDescription}
                                    onClick={() => setPage('collection')}
                                >
                                    {translate('components.create.modal.collection.new.button')}
                                </Button>
                            </Form.Group>
                        </Form>
                    </ModalBase.Content>
                    <ModalBase.Footer
                        actionPrimary={
                            <Button
                                variant="primary"
                                onClick={() =>
                                    onSave({
                                        name,
                                        description,
                                        collectionId,
                                        collectionName,
                                        id: component?.id,
                                    })
                                }
                                isDisabled={isSaveDisabled()}
                                isLoading={isSaving}
                            >
                                {translate('components.create.modal.save')}
                            </Button>
                        }
                        actionSecondary={
                            <Button variant="secondary" onClick={onClose}>
                                {translate('components.create.modal.cancel')}
                            </Button>
                        }
                    />
                </>
            )}
            {page === 'collection' && (
                <>
                    <ModalBase.Header
                        title={translate('components.create.modal.collection.title')}
                        backButton={{
                            title: translate('components.create.modal.back'),
                            onClick: () => setPage('component'),
                        }}
                    />
                    <ModalBase.Content>
                        <Form>
                            <Form.Group>
                                <Form.Label htmlFor="collection-new-name">
                                    {translate('components.create.modal.newCollection.name.label')}
                                </Form.Label>
                                <Input
                                    value={collectionName}
                                    type="text"
                                    placeholder={translate('components.create.modal.newCollection.name.placeholder')}
                                    id="collection-new-name"
                                    aria-describedby="collection-new-name-helper-text"
                                    hasClearButton
                                    clearButtonProps={{
                                        'aria-label': translate(
                                            'components.create.modal.newCollection.name.clearButton',
                                        ),
                                    }}
                                    onChange={(value) => {
                                        setCollectionName(value);
                                        setCollectionNameDirty(true);
                                    }}
                                    isInvalid={(isEmptyCollectionName && collectionNameDirty) || isLongCollectionName}
                                />
                                {((isEmptyCollectionName && collectionNameDirty) || isLongCollectionName) && (
                                    <Form.HelperText id="collection-new-name-helper-text" isInvalid>
                                        {isLongCollectionName
                                            ? translate('components.create.modal.newCollection.name.error.too-long')
                                            : translate('components.create.modal.newCollection.name.error.empty')}
                                    </Form.HelperText>
                                )}
                            </Form.Group>
                        </Form>
                    </ModalBase.Content>
                    <ModalBase.Footer
                        actionPrimary={
                            <Button
                                variant="primary"
                                onClick={() =>
                                    onSave({
                                        name,
                                        description,
                                        collectionId,
                                        collectionName,
                                        id: component?.id,
                                    })
                                }
                                isDisabled={
                                    isEmptyName ||
                                    isLongName ||
                                    isLongDescription ||
                                    isEmptyCollectionName ||
                                    isLongCollectionName ||
                                    isSaving
                                }
                                isLoading={isSaving}
                            >
                                {translate('components.create.modal.save')}
                            </Button>
                        }
                        actionSecondary={
                            <Button variant="secondary" onClick={() => setPage('component')}>
                                {translate('components.create.modal.cancel')}
                            </Button>
                        }
                    />
                </>
            )}
        </ModalBase>
    );
};

export default memo(ComponentModal);
